import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { PenPortraitInsightVariable, Variable } from "@/types";
import { blackOverlay, toIndex, toPercentage, whiteOverlay } from "@/utils";

interface PenPortraitCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  score: string;
  levels: number[];
  onRemoveSection?: () => void;
  editable: boolean;
}

const PenPortraitCard: React.FC<PenPortraitCardProps> = ({
  title,
  variables,
  score,
  levels,
  onRemoveSection,
  editable
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  function useElementWidth() {
    const [width, setWidth] = useState<number>(275);
    const observerRef = useRef<ResizeObserver | null>(null);

    const ref = useCallback((node: HTMLElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      if (node) {
        const observer = new ResizeObserver(([entry]) => {
          setWidth(entry.contentRect.width);
        });

        observer.observe(node);
        observerRef.current = observer;
      }
    }, []);

    // Cleanup
    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, []);

    return [ref, width] as const;
  }

  // Get the size of the card
  const [cardRef, cardWidth] = useElementWidth();

  // Calculate the size of the images
  const numImages = cardWidth > 264 ? 6 : 4
  const imageSize = Math.max(Math.floor((cardWidth - 32) / numImages) - 8, 32);

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
      .filter((x) => levels.includes(x.tag_level));
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables, levels]);

  const overlay = useCallback((variable: PenPortraitInsightVariable) => {
    if (variable.tag_type === 'Interests') {
      if (theme.palette.mode === 'dark') {
        return whiteOverlay(0.15)
      } else {
        return blackOverlay(0.15)
      }
    } else {
      return undefined
    }
  }, [])

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Grid size={12} key={title}>
        <Card
          ref={cardRef}
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: theme.palette.divider,
            height: `${56 + 32 + imageSize + 34}px`,
            position: "relative",
            ":hover": {
              borderColor: editable ? theme.palette.primary.main : undefined,
            },
          }}
        >
          {editable &&
            <HelpBox title="Remove section">
              <IconButton
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 1,
                  p: 0.5,
                  borderRadius: "6px",
                  color: theme.palette.divider,
                }}
                onClick={onRemoveSection}
              >
                <Close fontSize="small" />
              </IconButton>
            </HelpBox>
          }
          <CardHeader
            title={title}
            slotProps={{ title: { variant: "body2" } }}
            sx={{ height: "56px", alignItems: "flex-start", pb: 0 }}
          />
          <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>
            <Grid container spacing={1} justifyContent="center">
              {scores.slice(0, numImages).map((variable) => (
                <Grid key={`${title}-${variable.tag_id}`}>
                  <HelpBox
                    title={`${variable.tag_name} (${formatFunc(
                      Number(
                        variable[score as keyof PenPortraitInsightVariable],
                      ),
                    )})`}
                  >
                    <Stack spacing={1} sx={{ width: imageSize }}>
                      <Box
                        component="img"
                        src={variable.image}
                        alt={variable.tag_name}
                        height={imageSize}
                        width={imageSize}
                        display="block"
                        style={{
                          borderRadius: 5,
                          padding: variable.tag_type === "Interests" ? imageSize / 10 : undefined,
                          backgroundColor: theme.palette.background.paper,
                          backgroundImage: overlay(variable),
                        }}
                      ></Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "8px",
                          textAlign: "center",
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                        }}
                      >
                        {variable.tag_name}
                      </Typography>
                    </Stack>
                  </HelpBox>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  }
};

export default PenPortraitCard;
